import { useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import type { UserRole } from "../types/auth.types";
import { canAccessMenuItem } from "../config/menuPermissions";
import { canAccessPath } from "../utils/permissions";
import { userHasRouteAccess } from "../utils/roleUtils";

/**
 * Custom hook exposing auth state and RBAC helpers using exact backend roles.
 */
export function useAuthState() {
  const auth = useAuth();

  const permissions = useMemo(
    () => ({
      hasRole: (targetRole: UserRole) => auth.hasRole(targetRole),
      hasAnyRole: (roles: UserRole[]) => auth.hasAnyRole(roles),
      canAccessRoute: (path: string) => auth.canAccessRoute(path),
      canAccessMenu: (menuId: string) => auth.canAccessMenu(menuId),
      canAccessWithRoles: (allowedRoles: UserRole[]) =>
        userHasRouteAccess(auth.role, allowedRoles),
      canAccessPath: (path: string) => canAccessPath(path, auth.role),
      canAccessMenuItem: (menuId: string) =>
        canAccessMenuItem(menuId, auth.role),
    }),
    [auth]
  );

  return {
    ...auth,
    ...permissions,
  };
}

export default useAuthState;
